import { errorKind, Slug } from "@bundle/core";
import type { JoinableWorkspace } from "@bundle/contracts";
import { Avatar, Button, Divider, Icon, Stack, Text, TextField, useSession, useT } from "@bundle/ui";
import { useState } from "react";
import { AuthCard } from "./SignInScreen.tsx";

export type CreateWorkspaceScreenProps = {
  /** Shown before the slug preview, e.g. "bundle.app/". */
  addressPrefix: string;
};

type Failure = "offline" | "rateLimited" | "failed";

/**
 * First run (no workspaces) or "Create workspace" from the switcher. On success the session opens the new
 * workspace in #general and the shell offers "Invite people" (SessionState.welcome). Workspaces that allow my
 * verified email's domain are offered first ("Join your team"): joining opens them as a member.
 */
export function CreateWorkspaceScreen({ addressPrefix }: CreateWorkspaceScreenProps) {
  const t = useT();
  const { vm, state } = useSession();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure | undefined>(undefined);
  const slug = Slug.slugify(name);
  const hasOthers = (state.workspaces ?? []).length > 0;
  const joinable = state.joinable ?? [];
  const [joinFailure, setJoinFailure] = useState<{ id: string; offline: boolean } | undefined>(undefined);

  const join = (w: JoinableWorkspace) => {
    if (state.joining !== undefined) return;
    setJoinFailure(undefined);
    vm.joinWorkspace(w.id).catch((e: unknown) => setJoinFailure({ id: w.id, offline: errorKind(e) === "network" }));
  };

  const submit = () => {
    if (busy || name.trim() === "") return;
    setBusy(true);
    setFailure(undefined);
    vm.createWorkspace(name).then(
      () => setBusy(false),
      (e: unknown) => {
        setBusy(false);
        const kind = errorKind(e);
        setFailure(kind === "network" ? "offline" : kind === "rateLimited" ? "rateLimited" : "failed");
      },
    );
  };

  return (
    <AuthCard testID="create-workspace-screen">
      {joinable.length === 0 ? null : (
        <>
          <Stack gap="1">
            <Text variant="title2" weight="bold">
              {t("workspace.join.title")}
            </Text>
            <Text color="text.secondary">{t("workspace.join.subtitle")}</Text>
          </Stack>
          <Stack gap="3" testID="join-workspace-list">
            {joinable.map((w) => (
              <Stack key={w.id} gap="1">
                <Stack direction="horizontal" align="center" gap="3">
                  <Avatar name={w.name} src={w.iconUrl} square size="avatar.lg" />
                  <Stack grow gap="0.5">
                    <Text weight="semibold">{w.name}</Text>
                    <Text variant="footnote" color="text.secondary">
                      {`${t("workspace.join.domain", { domain: w.domain })} · ${t("workspace.join.members", { count: w.memberCount })}`}
                    </Text>
                  </Stack>
                  <Button
                    label={t("workspace.join.action")}
                    variant="primary"
                    loading={state.joining === w.id}
                    disabled={state.joining !== undefined && state.joining !== w.id}
                    onPress={() => join(w)}
                    testID={`join-workspace-${w.slug}`}
                  />
                </Stack>
                {joinFailure?.id === w.id ? (
                  <Stack direction="horizontal" gap="2" align="start">
                    <Icon name={joinFailure.offline ? "offline" : "alert"} size="icon.sm" color="status.danger" />
                    <Text variant="footnote" color="status.danger">
                      {joinFailure.offline ? t("workspace.join.offline") : t("workspace.join.error")}
                    </Text>
                  </Stack>
                ) : null}
              </Stack>
            ))}
          </Stack>
          <Divider />
        </>
      )}
      <Stack gap="1">
        <Text variant={joinable.length === 0 ? "title2" : "title3"} weight="bold">
          {joinable.length === 0 ? t("workspace.create.title") : t("workspace.join.orCreate")}
        </Text>
        <Text color="text.secondary">{t("workspace.create.subtitle")}</Text>
      </Stack>
      <Stack gap="2">
        <TextField
          label={t("workspace.create.name")}
          placeholder={t("workspace.create.placeholder")}
          value={name}
          onChange={setName}
          autoFocus
          onSubmit={submit}
          testID="create-workspace-name"
        />
        {name.trim() === "" ? null : (
          <Text variant="footnote" color="text.tertiary" testID="create-workspace-url">
            {t("workspace.create.url", { url: `${addressPrefix}${slug === "" ? Slug.FALLBACK_SLUG : slug}` })}
          </Text>
        )}
      </Stack>
      {failure === undefined ? null : (
        <Stack direction="horizontal" gap="2" align="start">
          <Icon name={failure === "offline" ? "offline" : "alert"} size="icon.sm" color="status.danger" />
          <Text variant="footnote" color="status.danger">
            {failure === "offline" ? t("workspace.create.offline") : failure === "rateLimited" ? t("workspace.create.rateLimited") : t("workspace.create.error")}
          </Text>
        </Stack>
      )}
      <Button label={t("workspace.create.submit")} variant="primary" size="lg" loading={busy} disabled={name.trim() === ""} onPress={submit} testID="create-workspace-submit" />
      <Stack direction="horizontal" justify="between" align="center" wrap>
        <Text variant="footnote" color="text.tertiary">
          {t("auth.signedInAs", { email: state.session?.email ?? "" })}
        </Text>
        {hasOthers ? (
          <Button label={t("common.cancel")} variant="ghost" size="sm" onPress={() => vm.cancelCreatingWorkspace()} testID="create-workspace-cancel" />
        ) : (
          <Button label={t("auth.signOut")} variant="ghost" size="sm" onPress={() => vm.signOut()} testID="create-workspace-sign-out" />
        )}
      </Stack>
    </AuthCard>
  );
}
